import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import {
  CreditCard, Calendar, Database, TrendingUp, Clock, AlertCircle,
  Plus, BarChart2, ArrowUpRight, ArrowDownRight, RefreshCw, Download,
} from 'lucide-react';

const usageChartData = [
  { date: 'Mon', credits: 32 },
  { date: 'Tue', credits: 45 },
  { date: 'Wed', credits: 28 },
  { date: 'Thu', credits: 18 },
  { date: 'Fri', credits: 52 },
  { date: 'Sat', credits: 12 },
  { date: 'Sun', credits: 8 },
];

const maxUsage = Math.max(...usageChartData.map((d) => d.credits));

const CreditBalancePage: React.FC = () => {
  const { balance, transactions, isLowBalance } = useCreditBalance();
  const [timeRange, setTimeRange] = useState('7_days');

  const transactionIcon = (type: string) => {
    switch (type) {
      case 'purchase': return { icon: ArrowUpRight, color: 'text-green-600 bg-green-50' };
      case 'usage': return { icon: ArrowDownRight, color: 'text-red-600 bg-red-50' };
      case 'refund': return { icon: RefreshCw, color: 'text-blue-600 bg-blue-50' };
      default: return { icon: RefreshCw, color: 'text-purple-600 bg-purple-50' };
    }
  };

  return (
    <ContentWrapper>
      <PageHeader
        title="Credit Balance"
        subtitle="Manage your prepaid credits for claims discovery processing"
        breadcrumbs={[
          { label: 'Account & Billing' },
          { label: 'Credit Balance', isActive: true },
        ]}
        actions={
          <a href="/account/purchase">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" /> Purchase Credits
            </Button>
          </a>
        }
      />

      {/* Low Balance Alert */}
      {isLowBalance && (
        <Alert variant="warning" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Low balance alert — Your credit balance is below 500. Consider purchasing more credits to avoid processing interruptions.
          </AlertDescription>
        </Alert>
      )}

      {/* Credit Overview Card */}
      <Card className={`border shadow-sm mb-8 ${isLowBalance ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <CreditCard className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Current Credit Balance</h2>
                <p className="text-gray-600">Prepaid credits for claims discovery processing</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-lg text-gray-600">Available Credits:</span>
                <span className="text-3xl font-bold text-green-600">{balance.available.toLocaleString()}</span>
              </div>
              {balance.reserved > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Reserved Credits:</span>
                  <span className="text-lg font-semibold text-yellow-600">{balance.reserved} (currently processing)</span>
                </div>
              )}
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span>Expires: <strong>{balance.expires}</strong> <span className="text-gray-500">(24 months from purchase)</span></span>
              </div>
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-gray-500" />
                <span>Processing Rate: <strong>{balance.processingRate}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-500" />
                <span>Average Usage: <strong>~{balance.averageUsage} credits/week</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>Estimated Time Remaining: <strong>{balance.estimatedWeeksRemaining} weeks at current rate</strong></span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Usage Analytics */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-purple-600" /> Usage Analytics
              </CardTitle>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="text-sm border border-gray-300 rounded-md px-3 py-1">
                <option value="7_days">Last 7 Days</option>
                <option value="30_days">Last 30 Days</option>
                <option value="90_days">Last 90 Days</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-end gap-2 h-40 mb-4">
              {usageChartData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-500">{d.credits}</span>
                  <div
                    className="w-full bg-purple-500 rounded-t transition-all hover:bg-purple-600"
                    style={{ height: `${(d.credits / maxUsage) * 100}%` }}
                  />
                  <span className="text-xs text-gray-500">{d.date}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
              <div><p className="text-xs text-gray-500">Daily Average</p><p className="font-semibold text-gray-900">21 credits</p></div>
              <div><p className="text-xs text-gray-500">Peak Day</p><p className="font-semibold text-gray-900">52 credits</p></div>
              <div><p className="text-xs text-gray-500">Accuracy Rate</p><p className="font-semibold text-green-600">97.2%</p></div>
              <div><p className="text-xs text-gray-500">Refunds</p><p className="font-semibold text-gray-900">8 credits</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-gray-900">Transaction History</CardTitle>
              <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Export</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {transactions.map((tx) => {
                const { icon: TxIcon, color } = transactionIcon(tx.type);
                return (
                  <div key={tx.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                    <div className={`p-2 rounded-lg ${color}`}>
                      <TxIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{tx.description}</p>
                      <p className="text-xs text-gray-500">{tx.date}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()} credits
                      </p>
                      <p className="text-xs text-gray-500">Bal: {tx.balance.toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Balance Protection */}
      <Card className="bg-green-50 border border-green-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-green-800 mb-3">Automatic Balance Protection</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-700">
            <p>✅ Real-time balance verification before processing</p>
            <p>✅ Cannot start jobs exceeding available credits</p>
            <p>✅ Reserved credits prevent double-spending</p>
            <p>✅ Failed jobs automatically refund reserved credits</p>
          </div>
        </CardContent>
      </Card>
    </ContentWrapper>
  );
};

export default CreditBalancePage;